import { useContext, useLayoutEffect, type RefObject } from 'react'
import { useLocation } from 'react-router-dom'
import { PageNavigationContext } from '@/app/navigation/page-context'

export function usePageScroll(content: RefObject<HTMLDivElement | null>, enabled: boolean) {
  const navigation = useContext(PageNavigationContext)
  const { pathname, search } = useLocation()
  useLayoutEffect(() => {
    const root = content.current
    const main = root?.closest<HTMLElement>('.app-main')
    if (!enabled || !root || !main || !navigation) return
    const key = pathname + search
    const target = navigation.getScroll(key)
    let restoring = true
    const restore = () => {
      if (!restoring) return
      main.scrollTop = target
      if (Math.abs(main.scrollTop - target) < 1) restoring = false
    }
    const save = () => { if (!restoring) navigation.setScroll(key, main.scrollTop) }
    const stopRestoring = () => { restoring = false }
    restore()
    // Restore after an uncached query or lazy image makes the page tall enough.
    const observer = new ResizeObserver(restore)
    observer.observe(root)
    main.addEventListener('scroll', save, { passive: true })
    main.addEventListener('wheel', stopRestoring, { passive: true })
    main.addEventListener('touchstart', stopRestoring, { passive: true })
    main.addEventListener('pointerdown', stopRestoring)
    main.addEventListener('keydown', stopRestoring)
    return () => {
      observer.disconnect()
      main.removeEventListener('scroll', save)
      main.removeEventListener('wheel', stopRestoring)
      main.removeEventListener('touchstart', stopRestoring)
      main.removeEventListener('pointerdown', stopRestoring)
      main.removeEventListener('keydown', stopRestoring)
    }
  }, [content, enabled, navigation, pathname, search])
}
